// src/components/manager/shared/BookingSystemForm.jsx
import React, { useState } from 'react';
import { Calendar, Settings, BarChart3, Users, Clock } from 'lucide-react';
import { ServiceManager } from '../booking/ServiceManager';
import { BookingCalendar } from '../booking/BookingCalendar';
import { BookingFormBuilder } from '../booking/BookingFormBuilder';

export const BookingSystemForm = ({ 
  bookId, 
  templateType = 'organisation', 
  data = {}, 
  onChange, 
  errors = {} 
}) => {
  const [activeTab, setActiveTab] = useState('services');

  const tabs = [
    { id: 'services', label: 'Services', icon: Settings },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'forms', label: 'Forms', icon: Users },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'services':
        return <ServiceManager bookId={bookId} onUpdate={onChange} />;
      case 'calendar':
        return <BookingCalendar bookId={bookId} onUpdate={onChange} />;
      case 'forms':
        return <BookingFormBuilder service={data.selectedService} onUpdate={onChange} />;
      case 'analytics':
        return <BookingAnalytics bookId={bookId} />;
      default:
        return <ServiceManager bookId={bookId} onUpdate={onChange} />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Booking System</h2>
        <p className="text-gray-600">Manage appointments, consultations, and facility bookings</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex justify-center space-x-8">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-5 w-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[500px]">
        {renderTabContent()}
      </div>
    </div>
  );
};

// Simple Analytics Component
const BookingAnalytics = ({ bookId }) => {
  const stats = {
    totalBookings: 156,
    thisWeek: 23,
    pending: 8,
    revenue: 45680,
    popularService: 'Consultation'
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900">Booking Analytics</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="text-3xl font-bold text-blue-600">{stats.totalBookings}</div>
          <div className="text-sm text-gray-500">Total Bookings</div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="text-3xl font-bold text-green-600">{stats.thisWeek}</div>
          <div className="text-sm text-gray-500">This Week</div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="text-3xl font-bold text-yellow-600">{stats.pending}</div>
          <div className="text-sm text-gray-500">Pending</div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="text-3xl font-bold text-purple-600">R{stats.revenue.toLocaleString()}</div>
          <div className="text-sm text-gray-500">Revenue</div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="text-lg font-bold text-indigo-600">{stats.popularService}</div>
          <div className="text-sm text-gray-500">Most Popular</div>
        </div>
      </div>
    </div>
  );
};